import React, { useEffect, useMemo, useRef, useState } from "react";
import { ArrowRight, CheckCircle2, Play, RotateCcw } from "lucide-react";

const fmt = (s) => `${Math.floor((s || 0) / 60)}:${String(Math.floor((s || 0) % 60)).padStart(2, "0")}`;

// Interactive guided-demo player: plays an MP4/WebM, pauses at each hotspot,
// highlights the area with a ripple, and shows the hint card ADJOINING the
// hotspot — beside it on desktop, above/below it on mobile (never off-frame).
// No native video controls: Begin starts playback; after completion the viewer
// gets Restart + per-hotspot jump chips.
export function HotspotVideoPlayer({ url, hotspots = [], description = "", onFinished }) {
  const videoRef = useRef(null);
  const frameRef = useRef(null);
  const [frameH, setFrameH] = useState(0);
  const sorted = useMemo(
    () => [...(hotspots || [])].sort((a, b) => a.t - b.t).map((h, i) => ({ ...h, _k: h.id || `hs${i}` })),
    [hotspots],
  );
  const [started, setStarted] = useState(false);
  const [seenIds, setSeenIds] = useState([]);
  const [active, setActive] = useState(null);
  const [ended, setEnded] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const fn = () => setIsMobile(mq.matches);
    mq.addEventListener("change", fn);
    return () => mq.removeEventListener("change", fn);
  }, []);

  // Measure the video frame so mobile hint cards can be clamped in px
  // (% max-height does not resolve against an auto-height absolute wrapper).
  useEffect(() => {
    if (!frameRef.current) return;
    const ro = new ResizeObserver((entries) => setFrameH(entries[0].contentRect.height));
    ro.observe(frameRef.current);
    return () => ro.disconnect();
  }, [url]);

  const finished = started && ended && sorted.every((h) => seenIds.includes(h._k));
  useEffect(() => {
    if (finished && onFinished) onFinished();
  }, [finished, onFinished]);

  const handleTimeUpdate = () => {
    const v = videoRef.current;
    if (!v || active) return;
    const next = sorted.find((h) => !seenIds.includes(h._k) && v.currentTime >= h.t);
    if (next) {
      v.pause();
      setSeenIds((s) => [...s, next._k]);
      setActive(next);
    }
  };

  const handleNext = () => {
    setActive(null);
    videoRef.current?.play().catch(() => {});
  };

  const begin = () => {
    setStarted(true);
    setEnded(false);
    videoRef.current?.play().catch(() => {});
  };

  const restart = () => {
    const v = videoRef.current;
    if (!v) return;
    setSeenIds([]);
    setActive(null);
    setEnded(false);
    v.currentTime = 0;
    v.play().catch(() => {});
  };

  const jumpTo = (h) => {
    const v = videoRef.current;
    if (!v) return;
    setSeenIds((s) => s.filter((id) => id !== h._k)); // let this hotspot fire again
    setActive(null);
    setEnded(false);
    v.currentTime = Math.max(0, h.t - 0.4);
    v.play().catch(() => {});
  };

  // Desktop: hint beside the box, on whichever side has more room.
  const desktopCardStyle = (h) => {
    const rightRoom = 100 - (h.x + h.w);
    const style = { top: `${Math.min(Math.max(h.y, 4), 55)}%` };
    if (rightRoom >= h.x) style.left = `calc(${h.x + h.w}% + 12px)`;
    else style.right = `calc(${100 - h.x}% + 12px)`;
    return style;
  };

  // Mobile: hint stays ON the video, directly above or below the box, clamped
  // inside the frame using the measured frame height (px).
  const mobileCardStyle = (h) => {
    const belowRoom = 100 - (h.y + h.h);
    const left = Math.min(Math.max(h.x, 4), 55);
    const px = (pct) => Math.max(64, Math.round((pct / 100) * frameH) - 12);
    if (Math.max(belowRoom, h.y) < 26) {
      return { pos: { bottom: "4%", left: "4%", right: "4%" }, maxH: `${px(60)}px` };
    }
    if (belowRoom >= h.y) {
      return { pos: { top: `calc(${h.y + h.h}% + 6px)`, left: `${left}%` }, maxH: `${px(belowRoom)}px` };
    }
    return { pos: { bottom: `calc(${100 - h.y}% + 6px)`, left: `${left}%` }, maxH: `${px(h.y)}px` };
  };

  const hintCard = (h, maxH) => (
    <div
      className={`rounded-2xl bg-[#FF5E1A] text-white shadow-xl ${isMobile ? "p-2.5 w-[min(200px,72%)]" : "p-3 w-[min(230px,80%)]"}`}
      style={maxH ? { maxHeight: maxH, overflowY: "auto" } : undefined}
      data-testid="ivp-hint-card"
    >
      <div className="text-[9px] font-bold uppercase tracking-wider text-white/70 mb-0.5">
        Hotspot {sorted.findIndex((s) => s._k === h._k) + 1} of {sorted.length}
      </div>
      <p className={`${isMobile ? "text-xs" : "text-sm"} font-medium leading-snug`}>{h.text || "Look here"}</p>
      <button
        onClick={handleNext}
        data-testid="ivp-next-btn"
        className={`${isMobile ? "mt-1.5 py-1.5 text-xs" : "mt-2.5 py-2 text-sm"} w-full inline-flex items-center justify-center gap-1 rounded-xl bg-white text-[#E04F00] px-3 font-bold hover:bg-orange-50 transition-colors`}
      >
        Next <ArrowRight className="w-3.5 h-3.5" />
      </button>
    </div>
  );

  if (!url) {
    return <div className="rounded-2xl bg-slate-100 p-6 text-center text-slate-500 text-sm">No video URL set.</div>;
  }

  return (
    <div>
      <div className="relative rounded-2xl overflow-hidden bg-slate-900" ref={frameRef}>
        <video
          ref={videoRef}
          src={url}
          playsInline
          preload="metadata"
          className="w-full"
          onTimeUpdate={handleTimeUpdate}
          onEnded={() => setEnded(true)}
          onPlay={() => { if (active) videoRef.current?.pause(); }}
          data-testid="ivp-video"
        />

        {/* Begin overlay */}
        {!started && (
          <button
            onClick={begin}
            data-testid="ivp-begin-btn"
            className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 bg-slate-900/60 text-white"
          >
            <span className="w-16 h-16 rounded-full bg-[#FF5E1A] flex items-center justify-center shadow-xl">
              <Play className="w-7 h-7 ml-1" />
            </span>
            <span className="font-bold text-lg">Begin</span>
          </button>
        )}

        {/* Active hotspot: ripple box + adjoining hint */}
        {active && (
          <div
            className="absolute rounded-xl border-2 border-[#FF5E1A] cpn-hotspot-ripple pointer-events-none"
            style={{ left: `${active.x}%`, top: `${active.y}%`, width: `${active.w}%`, height: `${active.h}%` }}
            data-testid="ivp-hotspot-box"
          />
        )}
        {active && (() => {
          const m = isMobile ? mobileCardStyle(active) : null;
          return (
            <div className="absolute z-10" style={isMobile ? m.pos : desktopCardStyle(active)}>
              {hintCard(active, m?.maxH)}
            </div>
          );
        })()}

        {/* Completed: restart + jump points */}
        {started && ended && !active && (
          <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 bg-slate-900/70 p-4" data-testid="ivp-complete-overlay">
            <div className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600/90 text-white px-3 py-1 text-xs font-bold" data-testid="ivp-complete-badge">
              <CheckCircle2 className="w-3.5 h-3.5" /> Video complete
            </div>
            <div className="flex flex-wrap justify-center gap-2 max-w-md">
              <button onClick={restart} data-testid="ivp-restart"
                className="inline-flex items-center gap-1.5 rounded-full bg-white text-[#0F172A] px-4 py-2 text-xs font-bold">
                <RotateCcw className="w-3.5 h-3.5" /> Restart
              </button>
              {sorted.map((h, i) => (
                <button key={h._k} onClick={() => jumpTo(h)} data-testid={`ivp-jump-${i}`}
                  className="inline-flex items-center gap-1.5 rounded-full bg-white/15 border border-white/30 text-white px-3 py-2 text-xs font-semibold hover:bg-white/25">
                  <Play className="w-3 h-3" /> {fmt(h.t)} · {(h.text || `Point ${i + 1}`).slice(0, 26)}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {description ? (
        <div
          className="mt-3 glass-soft rounded-2xl p-4 max-h-40 overflow-y-auto text-sm text-slate-600 whitespace-pre-wrap"
          data-testid="ivp-description"
        >
          {description}
        </div>
      ) : null}
    </div>
  );
}
